// frontend/src/pages/Partners/PartnerDetail.jsx
import { useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import {
  getPartner,
  updatePartner,
  uploadPartnerDocument,
  getPartnerDocumentUrl,
  revokePartner,
  restorePartner,
  deletePartner,
} from "../../api/partner.api.js";
import {
  Spinner,
  StatusBadge,
  DocTypeLabel,
  formatDate,
  formatFileSize,
  ErrorMessage,
} from "../../components/ui";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  ShieldOff,
  ShieldCheck,
  Trash2,
  X,
  FileText,
  Pencil,
} from "lucide-react";

const DOC_TYPES = [
  "AKTA",
  "SK_KEMENKUMHAM",
  "NIB",
  "NPWP",
  "SHAREHOLDER_STRUCTURE",
];

// ─── Document row ─────────────────────────────────────────────────────────────

function DocRow({ doc, partnerId }) {
  const [loading, setLoading] = useState(false);

  async function download() {
    setLoading(true);
    try {
      const { url } = await getPartnerDocumentUrl(partnerId, doc.id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">
          {doc.fileName}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {formatFileSize(doc.fileSize)} · {formatDate(doc.uploadedAt)}
        </p>
      </div>
      <button
        onClick={download}
        disabled={loading}
        className="ml-3 flex items-center gap-1.5 text-xs btn-secondary py-1.5 px-3 flex-shrink-0"
      >
        <Download size={13} />
        {loading ? "Getting link..." : "Download"}
      </button>
    </div>
  );
}

// ─── Edit panel ───────────────────────────────────────────────────────────────

function EditPanel({ partner, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    companyName: partner.companyName ?? "",
    picName: partner.picName ?? "",
    picPhone: partner.picPhone ?? "",
    npwp: partner.npwp ?? "",
    address: partner.address ?? "",
  });
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => updatePartner(partner.id, form),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Edit Info</h3>
        {open && (
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        )}
      </div>

      {!open ? (
        <button
          onClick={() => {
            setForm({
              companyName: partner.companyName ?? "",
              picName: partner.picName ?? "",
              picPhone: partner.picPhone ?? "",
              npwp: partner.npwp ?? "",
              address: partner.address ?? "",
            });
            setOpen(true);
          }}
          className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
        >
          <Pencil size={14} /> Edit Partner Info
        </button>
      ) : (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}

          <div>
            <label className="label text-xs">Company Name</label>
            <input className="input text-sm" value={form.companyName} onChange={set("companyName")} />
          </div>
          <div>
            <label className="label text-xs">PIC Name</label>
            <input className="input text-sm" value={form.picName} onChange={set("picName")} />
          </div>
          <div>
            <label className="label text-xs">PIC Phone</label>
            <input className="input text-sm" value={form.picPhone} onChange={set("picPhone")} />
          </div>
          <div>
            <label className="label text-xs">NPWP</label>
            <input className="input text-sm" value={form.npwp} onChange={set("npwp")} />
          </div>
          <div>
            <label className="label text-xs">Address</label>
            <textarea className="input text-sm" rows={2} value={form.address} onChange={set("address")} />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary flex-1 text-sm py-1.5 disabled:opacity-50"
            >
              {save.isPending ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={() => setOpen(false)} className="btn-secondary text-sm px-3">
              <X size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Access panel (revoke / restore) ─────────────────────────────────────────

function AccessPanel({ partner, onDone }) {
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokePartner(partner.id),
    onSuccess: () => {
      setConfirmRevoke(false);
      onDone();
    },
    onError: (err) => alert(err.message),
  });

  const restore = useMutation({
    mutationFn: () => restorePartner(partner.id),
    onSuccess: onDone,
    onError: (err) => alert(err.message),
  });

  return (
    <div className="card p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Account Access</h3>
      {partner.isActive ? (
        <>
          <div className="flex items-center gap-2 text-green-700 mb-3">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-sm font-medium">Active</span>
          </div>
          {!confirmRevoke ? (
            <button
              onClick={() => setConfirmRevoke(true)}
              className="w-full flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors"
            >
              <ShieldOff size={14} /> Revoke Access
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Partner will no longer be able to log in.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => revoke.mutate()}
                  disabled={revoke.isPending}
                  className="flex-1 py-2 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {revoke.isPending ? "Revoking..." : "Confirm"}
                </button>
                <button
                  onClick={() => setConfirmRevoke(false)}
                  className="btn-secondary text-sm px-3"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-red-600 mb-3">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-sm font-medium">Access Revoked</span>
          </div>
          <button
            onClick={() => restore.mutate()}
            disabled={restore.isPending}
            className="w-full flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
          >
            <ShieldCheck size={14} />
            {restore.isPending ? "Restoring..." : "Restore Access"}
          </button>
        </>
      )}
    </div>
  );
}

// ─── Delete panel ─────────────────────────────────────────────────────────────

function DeletePanel({ partner }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const del = useMutation({
    mutationFn: () => deletePartner(partner.id),
    onSuccess: () => navigate("/partners"),
    onError: (err) => alert(err.message),
  });

  return (
    <div className="card p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Danger Zone</h3>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 text-sm py-2 px-4 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={14} /> Delete Partner
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            Permanently deletes{" "}
            <span className="font-semibold">{partner.companyName}</span> and all
            their documents. Cannot be undone. Blocked if active agreements
            exist.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="flex-1 py-2 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {del.isPending ? "Deleting..." : "Confirm Delete"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary text-sm px-3"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerDetail() {
  const { id } = useParams();
  const { isAdmin, isMaster, user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploadType, setUploadType] = useState("AKTA");
  const [uploadError, setUploadError] = useState(null);

  const isOwn = user?.userId === id;
  const canUpload = isMaster || isOwn;

  const { data: partner, isLoading } = useQuery({
    queryKey: ["partner", id],
    queryFn: () => getPartner(id),
  });

  const upload = useMutation({
    mutationFn: ({ file, type }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      return uploadPartnerDocument(id, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["partner", id] });
      setUploadError(null);
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (err) => setUploadError(err),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["partner", id] });
  }

  if (isLoading) return <Spinner />;
  if (!partner) return <p className="text-gray-500">Partner not found.</p>;

  const docsByType = DOC_TYPES.reduce((acc, t) => {
    acc[t] = (partner.documents ?? []).filter((d) => d.type === t);
    return acc;
  }, {});

  const activeAgreements =
    partner.agreements?.filter((a) => a.status === "APPROVED") ?? [];
  const totalAgreements = partner.agreements?.length ?? 0;
  const pastAgreements =
    partner.agreements?.filter((a) =>
      ["TERMINATED", "EXPIRED"].includes(a.status),
    ) ?? [];

  return (
    <div>
      <Link
        to={isAdmin ? "/partners" : "/dashboard"}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} />
        {isAdmin ? "Back to partners" : "Back to dashboard"}
      </Link>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {partner.companyName}
            </h1>
            {!partner.isActive && (
              <span className="badge bg-red-100 text-red-700">
                Access Revoked
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            {partner.picName}
            {partner.email && ` · ${partner.email}`}
            {partner.createdAt &&
              ` · Partner since ${formatDate(partner.createdAt)}`}
          </p>
        </div>
      </div>

      {/* ── Stats row ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          [
            "Active Agreements",
            activeAgreements.length,
            activeAgreements.length === 1 ? "agreement" : "agreements",
          ],
          [
            "Total Agreements",
            totalAgreements,
            totalAgreements === 1 ? "agreement" : "agreements",
          ],
          [
            "Completed",
            pastAgreements.length,
            pastAgreements.length === 1 ? "agreement" : "agreements",
          ],
        ].map(([label, value, unit]) => (
          <div key={label} className="card p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{unit}</p>
            <p className="text-xs font-medium text-gray-700 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Main: details + agreements + documents ────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          {/* Company details */}
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              Company Details
            </h2>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["PIC Name", partner.picName],
                ["Phone", partner.picPhone],
                ["Email", partner.email],
                ["NPWP", partner.npwp],
                ["Address", partner.address],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                      {k}
                    </dt>
                    <dd className="text-gray-900 font-medium break-all">{v}</dd>
                  </div>
                ))}
            </dl>
          </div>

          {/* Agreement history */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Agreement History</h2>
              {isAdmin && (
                <Link
                  to={`/agreements?partnerId=${id}`}
                  className="text-xs text-brand-500 hover:underline flex items-center gap-1"
                >
                  <FileText size={12} /> See all
                </Link>
              )}
            </div>
            {!partner.agreements?.length ? (
              <p className="px-6 py-8 text-sm text-gray-400 text-center">
                No agreements yet
              </p>
            ) : (
              <div className="divide-y divide-gray-50">
                {partner.agreements.map((a) => (
                  <Link
                    key={a.id}
                    to={`/agreements/${a.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {a.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {a.entityCache?.name ?? (
                          <span className="italic text-gray-400">
                            No entity
                          </span>
                        )}
                        {a.startDate && a.endDate && (
                          <span>
                            {" "}
                            · {formatDate(a.startDate)} –{" "}
                            {formatDate(a.endDate)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <StatusBadge status={a.status} />
                      <ChevronRight size={14} className="text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Documents</h2>
              {canUpload && (
                <div className="flex items-center gap-2">
                  <select
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="input py-1.5 text-xs w-40"
                  >
                    {DOC_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={upload.isPending}
                    className="btn-primary flex items-center gap-1.5 text-xs py-1.5 px-3"
                  >
                    <Upload size={13} />
                    {upload.isPending ? "Uploading..." : "Upload"}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) upload.mutate({ file, type: uploadType });
                    }}
                  />
                </div>
              )}
            </div>

            {uploadError && (
              <div className="px-6 pt-4">
                <ErrorMessage error={uploadError} />
              </div>
            )}

            <div className="px-6 py-4 space-y-5">
              {DOC_TYPES.map((type) => (
                <div key={type}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    <DocTypeLabel type={type} />
                  </p>
                  {docsByType[type].length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Not uploaded</p>
                  ) : (
                    <div className="space-y-2">
                      {docsByType[type].map((doc) => (
                        <DocRow key={doc.id} doc={doc} partnerId={id} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Sidebar: info + actions ───────────────────────────────────── */}
        <div className="space-y-4">
          {/* Registration info */}
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Registration</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500 uppercase tracking-wide">
                  Partner Since
                </dt>
                <dd className="text-gray-900 font-medium mt-0.5">
                  {formatDate(partner.createdAt)}
                </dd>
              </div>
              {partner.lastLogin && (
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">
                    Last Login
                  </dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {formatDate(partner.lastLogin)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Edit info — master admin or own partner profile */}
          {(isMaster || isOwn) && (
            <EditPanel partner={partner} onDone={invalidate} />
          )}

          {/* Revoke / restore — master only */}
          {isMaster && <AccessPanel partner={partner} onDone={invalidate} />}

          {/* Delete — master only */}
          {isMaster && <DeletePanel partner={partner} />}
        </div>
      </div>
    </div>
  );
}
